/**
 * memegen.link public API — free meme render URLs (CORS: *).
 * @see https://memegen.link/
 */

const MEMEGEN_ORIGIN = 'https://api.memegen.link'

/** Priority ordering — classics and office-safe favorites first. */
export const PRIORITY_TEMPLATE_IDS = [
  'drake', 'db', 'fine', 'doge', 'gb', 'gru', 'harold', 'success', 'woman-cat', 'pooh',
  'blb', 'ss', 'ggg', 'fry', 'mordor', 'cmm', 'spongebob', 'stonks', 'panik-kalm-panik',
  'wonka', 'philosoraptor', 'yuno', 'tenguy', 'fwp', 'bad', 'cb', 'awkward', 'awesome',
  'fa', 'facepalm', 'grumpycat', 'keanu', 'kermit', 'morpheus', 'interesting', 'office',
  'midwit', 'cheems', 'disastergirl', 'bender', 'buzz', 'boat', 'captain', 'cake',
  'handshake', 'khaby-lame', 'rollsafe', 'saltbae', 'same', 'stop', 'vince', 'wallet',
]

const BLOCKED_TEMPLATE_TERMS = [
  'nsfw',
  'nude',
  'porn',
  'sexy',
  'sex',
  'boobs',
  'ass',
  'strip',
  'horny',
  'onlyfans',
]

/** memegen.link path encoding (spaces → _, hyphens escaped). */
export function encodeMemeText(text) {
  const trimmed = String(text ?? '').trim()
  if (!trimmed) return '-'
  return trimmed.replace(/-/g, '--').replace(/ /g, '_')
}

/**
 * Build a rendered meme image URL.
 * @param {string} templateId
 * @param {string[]} captions — one entry per template line (use '' for blank)
 * @param {string} [ext='jpg']
 */
export function buildMemeImageUrl(templateId, captions, ext = 'jpg') {
  if (!templateId) throw new Error('templateId required')
  const lineCount = Math.max(1, captions.length)
  const segments = []
  for (let i = 0; i < lineCount; i += 1) {
    segments.push(encodeMemeText(captions[i] ?? ''))
  }
  return `${MEMEGEN_ORIGIN}/images/${encodeURIComponent(templateId)}/${segments.join('/')}.${ext}`
}

export function blankTemplateUrl(template) {
  if (template?.blank) return template.blank
  const id = template?.id ?? template
  return `${MEMEGEN_ORIGIN}/images/${encodeURIComponent(id)}.jpg`
}

export async function fetchMemeTemplates() {
  const res = await fetch(`${MEMEGEN_ORIGIN}/templates/`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to load meme templates (HTTP ${res.status})`)
  }
  const templates = await res.json()
  if (!Array.isArray(templates)) {
    throw new Error('Unexpected templates response')
  }
  return sortTemplates(templates.filter(isPublicSfwTemplate))
}

export function isPublicSfwTemplate(template) {
  const searchable = [
    template?.id,
    template?.name,
    ...(Array.isArray(template?.keywords) ? template.keywords : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return !BLOCKED_TEMPLATE_TERMS.some((term) => searchable.includes(term))
}

export function templateFormat(template) {
  const lines = Number(template?.lines ?? 1)
  if (lines <= 1) return 'single'
  if (lines === 2) return 'classic'
  return 'multi'
}

export function sortTemplates(templates) {
  const rank = new Map(PRIORITY_TEMPLATE_IDS.map((id, i) => [id, i]))
  return [...templates].sort((a, b) => {
    const ra = rank.has(a.id) ? rank.get(a.id) : 9999
    const rb = rank.has(b.id) ? rank.get(b.id) : 9999
    if (ra !== rb) return ra - rb
    return (a.name || a.id).localeCompare(b.name || b.id)
  })
}

export function initialCaptions(template) {
  const count = Math.max(1, template?.lines ?? 1)
  const example = template?.example?.text
  if (Array.isArray(example) && example.length >= count) {
    return example.slice(0, count)
  }
  return Array.from({ length: count }, () => '')
}

export function lineLabel(template, index) {
  const example = template?.example?.text?.[index]
  if (example && example.length <= 24) {
    return example
  }
  if ((template?.lines ?? 1) === 1) return 'Caption'
  if ((template?.lines ?? 1) === 2) {
    return index === 0 ? 'Top text' : 'Bottom text'
  }
  return `Line ${index + 1}`
}

export async function downloadMemeImage(url, filename) {
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
